

import React from 'react'
import { Card, Row, Col, Typography, Space, Tag, Table, Alert } from "antd";
import moment from "moment";
import { 
  WarningOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
const {Text } = Typography;

// CRB Credit Information Component
const CreditInformationSection = ({rider}) => {
    const crbData = rider?.latest_crb_response;


    const getCreditGradeBadge = (grade) => {
        const gradeInfo = {
          'AA': { color: 'green', range: '613+' },
          'BB': { color: 'lime', range: '590-612' },
          'CC': { color: 'orange', range: '574-589' },
          'DD': { color: 'gold', range: '562-573' },
          'EE': { color: 'yellow', range: '547-561' },
          'FF': { color: 'orange', range: '521-546' },
          'GG': { color: 'red', range: '516-520' },
          'HH': { color: 'red', range: '482-515' },
          'II': { color: 'red', range: '468-481' },
          'JJ': { color: 'red', range: '1-467' },
          'YY': { color: 'gray', range: 'No accounts / Not enough information' },
          'Z1': { color: 'purple', range: 'Under 18 years of age' },
          'Z2': { color: 'volcano', range: 'Individual is deceased' }
        };
      
        const info = gradeInfo[grade] || { color: 'default', range: 'Unknown' };
        
        return (
          <div style={{ 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px'
          }}>
            <Tag 
              color={info.color} 
              style={{ 
                marginBottom: '0',
                fontSize: 'clamp(12px, 2.5vw, 14px)', 
                fontWeight: 'bold',
                padding: '2px 8px'
              }}
            >
              {grade}
            </Tag>
            <div style={{ 
              fontSize: 'clamp(9px, 2vw, 11px)', 
              color: '#666', 
              lineHeight: '1.2',
              textAlign: 'center',
              wordBreak: 'break-word',
              maxWidth: '100%',
              padding: '0 4px'
            }}>
              {info.range}
            </div>
          </div>
        );
      };
    
      const getAccountStatusBadge = (status) => {
        const statusColors = {
          'Active': 'green',
          'Delinquent': 'red',
          'Closed': 'gray',
          'Default': 'red'
        };
        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
      };
    
    
    if (!crbData) {
      return (
        <Alert
          message="No Credit Information Available"
          description="CRB data has not been retrieved for this applicant."
          type="info"
          showIcon
        />
      );
    }

    const loanColumns = [
      {
        title: 'Account No',
        dataIndex: 'account_no',
        key: 'account_no',
        width: 120,
        render: (text) => <Text code>{text}</Text>
      },
      {
        title: 'Status',
        dataIndex: 'account_status',
        key: 'account_status',
        render: (status) => getAccountStatusBadge(status)
      },
      {
        title: 'Type',
        dataIndex: 'account_type',
        key: 'account_type',
      },
      {
        title: 'Principal Amount',
        dataIndex: 'principal_amount',
        key: 'principal_amount',
        render: (amount) => `${amount?.toLocaleString()} RWF`
      },
      {
        title: 'Balance',
        dataIndex: 'balance_amount',
        key: 'balance_amount',
        render: (amount) => <Text strong>{amount?.toLocaleString()} RWF</Text>
      },
      {
        title: 'Arrear Amount',
        dataIndex: 'arrear_amount',
        key: 'arrear_amount',
        render: (amount) => amount > 0 ? 
          <Text type="danger">{amount?.toLocaleString()} RWF</Text> : 
          <Text type="success">0 RWF</Text>
      },
      {
        title: 'Arrear Days',
        dataIndex: 'arrear_days',
        key: 'arrear_days',
        render: (days) => days > 0 ? 
          <Tag color="red">{days} days</Tag> : 
          <Tag color="green">0 days</Tag>
      },
      {
        title: 'Opening Date',
        dataIndex: 'account_opening_date',
        key: 'account_opening_date',
        render: (date) => moment(date).format('DD/MM/YYYY')
      }
    ];

    return (
      <Row gutter={[24, 24]}>
        {/* CRB Summary */}
        <Col xs={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center">
                <div className="mb-2">
                  {getCreditGradeBadge(crbData.grade)}
                </div>
                <Text type="secondary">Credit Grade</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center">
                <div className="text-2xl font-bold mb-2 text-blue-600">
                  {crbData.positive_score}
                </div>
                <Text type="secondary">Positive Score</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center">
                <div className="text-2xl font-bold mb-2 text-green-600">
                  {crbData.probability? crbData.probability : 'N/A'}
                </div>
                <Text type="secondary">Success Probability</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="text-center">
                <div className="text-2xl font-bold mb-2 text-orange-600">
                  {crbData.loan_count}
                </div>
                <Text type="secondary">Active Loans</Text>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Financial Overview */}
        <Col xs={24} md={12}>
          <Card type="inner" title="Financial Overview">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Total Current Balance</Text>
                <div className="text-xl font-bold text-red-600">
                  {crbData.total_current_balance?.toLocaleString()} RWF
                </div>
              </div>
              <div>
                <Text type="secondary">Has Current Loans</Text>
                <div>
                  {crbData.has_current_loans ? (
                    <Tag icon={<WarningOutlined />} color="orange">Yes</Tag>
                  ) : (
                    <Tag icon={<CheckCircleOutlined />} color="green">No</Tag>
                  )}
                </div>
              </div>
              <div>
                <Text type="secondary">CRB Check Date</Text>
                <div>{moment(crbData.created_at).format('DD/MM/YYYY HH:mm')}</div>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Risk Factors */}
        <Col xs={24} md={24}>
          <Card type="inner" title="Risk Assessment Factors">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div className="flex flex-col justify-between">
                <Text>Payment History:</Text>
                <Tag color="blue">{crbData.reason_code_aarc1}</Tag>
              </div>
              <div className="flex flex-col justify-between">
                <Text>Credit Utilization:</Text>
                <Tag color="blue">{crbData.reason_code_aarc2}</Tag>
              </div>
              <div className="flex flex-col justify-between">
                <Text>Credit Length:</Text>
                <Tag color="blue">{crbData.reason_code_aarc3}</Tag>
              </div>
              <div className="flex flex-col justify-between">
                <Text>Credit Inquiries:</Text>
                <div color="blue">{crbData.reason_code_aarc4}</div>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Loan Details Table */}
        {crbData.current_loans && crbData.current_loans.length > 0 && (
          <Col xs={24}>
            <Card type="inner" title={`Current Loans (${crbData.current_loans.length})`}>
              <Table
                dataSource={crbData.current_loans}
                columns={loanColumns}
                rowKey="id"
                scroll={{ x: 1000 }}
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
        )}

        {/* CRB Response Status */}
        <Col xs={24}>
          <Alert
            message={`CRB Response Status: ${crbData.response_message}`}
            description={`Response Code: ${crbData.response_code} | Success: ${crbData.success ? 'Yes' : 'No'}`}
            type={crbData.success ? 'success' : 'error'}
            showIcon
          />
        </Col>
      </Row>
    );
  };

  export default CreditInformationSection;